import { Field, PolygonType } from '#models/questionnaire';
import { useAddressField } from '#modules/LeadGeneration/hooks/addressField';
import { useFieldStyles, useLeadField } from '#modules/LeadGeneration/hooks/field';
import { getCoordinateById, googleAutocomplete } from '#utils/getAutoComplete';
import { isGooglePlacesLoaded } from '#utils/googleMaps';
import { Box, TextField, Typography } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { CheckOutlined } from '@material-ui/icons';
import * as React from 'react';

interface searchboxLocationType {
  field: Field;
  handleChangeFields?: (type: string, value: any, addressLocation?: any) => void;
  questionLocation: PolygonType[][];
}

export const AddressField = ({ field, handleChangeFields, questionLocation }: searchboxLocationType) => {
  const [formField, { error, touched }, { setValue, setTouched }] = useLeadField(field);
  const [{ value: locationValue }, { error: errorLocation }, { setValue: setValueLocation }] = useAddressField(
    field,
    questionLocation
  );

  const { stylesLabel, textField } = useFieldStyles();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = React.useState<any>();
  const [query, setQuery] = React.useState<string | undefined>('');

  const autocomplete = React.useMemo(() => {
    if (inputRef.current && isGooglePlacesLoaded()) {
      return new google.maps.places.Autocomplete(inputRef.current);
    }
    return;
  }, [inputRef.current]);

  React.useEffect(() => {
    if (formField.value && !query && formField.value !== query) {
      setQuery(formField.value);
      setTouched(true);
    }
  }, [formField.value]);

  React.useLayoutEffect(() => {
    if (searchValue) {
      if (searchValue?.geometry?.location && searchValue?.formatted_address) {
        const addressLocation = searchValue.geometry.location;
        const addressLat = addressLocation.lat();
        const addressLng = addressLocation.lng();
        if (handleChangeFields) {
          handleChangeFields(field.type, searchValue.formatted_address, { lat: addressLat, lng: addressLng });
        }
        setValue(searchValue.formatted_address);
        setValueLocation({ lat: addressLat, lng: addressLng });
        setSearchValue('');
      }
    }
  }, [searchValue]);

  React.useLayoutEffect(() => {
    if (inputRef.current) {
      autocomplete?.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address && place?.geometry?.location && inputRef.current) {
          setTimeout(() => {
            if (place.formatted_address !== formField.value) {
              setQuery(place.formatted_address);
              setSearchValue(place);
            }
          }, 500);
        }
      });
    }
  }, [inputRef.current]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setQuery(event.target.value);
    if (!event.target.value) {
      if (handleChangeFields) {
        handleChangeFields(field.type, '', null);
      }
      setValue('');
      setValueLocation('');
    }
  };

  React.useLayoutEffect(() => {
    const handleDebounce = setTimeout(async () => {
      try {
        if (!query || query === formField.value || searchValue) {
          return;
        }

        // If Google Maps is not available, just accept the text input without geolocation
        if (!isGooglePlacesLoaded()) {
          if (handleChangeFields) {
            handleChangeFields(field.type, query, null);
          }
          setValue(query);
          setValueLocation('');
          if (!touched) {
            setTouched(true);
          }
          return;
        }

        const locationSearch = await googleAutocomplete(query);
        if (locationSearch) {
          const location = (locationSearch as any[])[0];
          const locationDetail = (await getCoordinateById(location.place_id)) as google.maps.places.PlaceResult;
          if (locationDetail && locationDetail?.geometry?.location) {
            const addressLocation = {
              lat: locationDetail.geometry.location.lat(),
              lng: locationDetail.geometry.location.lng()
            };
            if (handleChangeFields) {
              handleChangeFields(field.type, query, addressLocation);
            }
            setValueLocation(addressLocation);
            setValue(query);
          }
        }
        if (!touched) {
          setTouched(true);
        }
      } catch (e) {
        console.error(e);
        // On error, still allow the text to be entered without geolocation
        if (handleChangeFields) {
          handleChangeFields(field.type, query, null);
        }
        setValue(query);
        setValueLocation('');
        if (!touched) {
          setTouched(true);
        }
      }
    }, 500);

    return () => {
      clearTimeout(handleDebounce);
    };
  }, [query]);

  return (
    <React.Fragment>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Typography variant='subtitle2' className={stylesLabel}>
          {field.title}
        </Typography>
      </Box>
      <TextField
        autoComplete='new-password'
        className={textField}
        value={query}
        placeholder={field.placeholderText}
        inputRef={inputRef}
        variant='outlined'
        size='small'
        fullWidth
        onChange={(event) => handleChange(event)}
        error={Boolean((errorLocation || (error && !formField.value)) && touched)}
        InputProps={{
          endAdornment: Boolean(!errorLocation && locationValue && touched) ? (
            <CheckOutlined htmlColor={green[500]} />
          ) : null,
          className: 'lead-input swyft-input'
        }}
      />
      {(errorLocation || (error && !formField.value)) && touched && (
        <Typography variant='subtitle2' color='error'>
          {errorLocation || error}
        </Typography>
      )}
      <div id='map' />
    </React.Fragment>
  );
};
